import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  DomainSetting,
  ProviderCapabilitiesOverrides,
} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {
  PaymentProvider,
  PaymentProviderCapabilities,
} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

const capabilityKeys: (keyof PaymentProviderCapabilities)[] = [
  'creditCardTokenization',
  'pixOutTransfer',
  'pixOutQrCode',
  'tedOutBankSlip',
  'tedOutTransfer',
  'pixKeyCheck',
];

@Component({
  selector: 'provider-capabilities-override-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeSelectV3Component,
  ],
  templateUrl: './provider-capabilities-override-parameter.component.html',
})
export class ProviderCapabilitiesOverrideParameterComponent implements OnInit {
  providerForm!: FormGroup;
  providerToAdd = new FormControl<PaymentProvider | null>(null);
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  draft: ProviderCapabilitiesOverrides = {};
  activeProvider: PaymentProvider | null = null;
  addProviderItems: {value: PaymentProvider; label: string}[] = [];

  readonly allProviders = Object.values(PaymentProvider);
  readonly capKeys = capabilityKeys;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
  }

  ngOnInit(): void {
    this.draft = {...this.parseValue()};
    this.providerForm = this.fb.group(this.defaultCapsValues({}));
    this.refreshAddProviderItems();
  }

  private refreshAddProviderItems(): void {
    this.addProviderItems = this.allProviders
      .filter(p => this.draft[p] === undefined)
      .map(p => ({value: p, label: p}));
  }

  get configuredProviders(): PaymentProvider[] {
    return Object.keys(this.draft).filter(
      k => this.draft[k as PaymentProvider] !== undefined,
    ) as PaymentProvider[];
  }

  private defaultCapsValues(
    cur: Partial<PaymentProviderCapabilities>,
  ): Record<string, boolean> {
    const caps: Record<string, boolean> = {};
    for (const k of capabilityKeys) {
      caps[k] = Boolean(cur[k]);
    }
    return caps;
  }

  private parseValue(): ProviderCapabilitiesOverrides {
    const raw = this.setting.value;
    if (!raw) return {};
    try {
      const obj =
        typeof raw === 'string'
          ? (JSON.parse(raw) as ProviderCapabilitiesOverrides)
          : (raw as ProviderCapabilitiesOverrides);
      return typeof obj === 'object' && obj !== null ? obj : {};
    } catch {
      return {};
    }
  }

  addProvider(): void {
    const p = this.providerToAdd.value;
    if (!p || this.draft[p] !== undefined) return;
    const caps: Partial<PaymentProviderCapabilities> = {};
    for (const k of capabilityKeys) {
      caps[k] = false;
    }
    this.draft = {...this.draft, [p]: caps};
    this.activeProvider = p;
    this.providerForm.patchValue(this.defaultCapsValues(caps));
    this.providerToAdd.setValue(null);
    this.refreshAddProviderItems();
  }

  editProvider(p: PaymentProvider): void {
    this.activeProvider = p;
    this.providerForm.patchValue(this.defaultCapsValues(this.draft[p] ?? {}));
  }

  removeProvider(p: PaymentProvider): void {
    const rest = {...this.draft};
    delete rest[p];
    this.draft = rest as ProviderCapabilitiesOverrides;
    if (this.activeProvider === p) {
      this.activeProvider = null;
      this.providerForm.reset(this.defaultCapsValues({}));
    }
    this.refreshAddProviderItems();
  }

  apply(): void {
    if (!this.activeProvider) return;
    const v = this.providerForm.value as Record<string, boolean>;
    const caps: Partial<PaymentProviderCapabilities> = {};
    for (const k of capabilityKeys) {
      caps[k] = Boolean(v[k]);
    }
    this.draft = {
      ...this.draft,
      [this.activeProvider]: caps,
    };
  }

  onSend(): void {
    if (this.submitting) return;
    if (this.activeProvider) {
      this.apply();
    }
    this.submitting = true;
    const out: ProviderCapabilitiesOverrides = {};
    for (const p of Object.keys(this.draft) as PaymentProvider[]) {
      const cur = this.draft[p] ?? {};
      const caps: Partial<PaymentProviderCapabilities> = {};
      for (const k of capabilityKeys) {
        caps[k] = Boolean(cur[k]);
      }
      out[p] = caps;
    }
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(out),
    };
    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
